"use client";
import { useEffect, useLayoutEffect, useState } from "react";

/**
 * useIsMobile
 * Standard: NUR Breakpoint-Check (width <= breakpoint).
 * Optional: Mit { smart: true } zusätzlich Device-Typ & Touch-Heuristik prüfen.
 *
 * @param {number} breakpoint - px-Grenze für Mobile-Layout (Default 1040)
 * @param {{ smart?: boolean, detailed?: boolean }} options
 *    smart:    wenn true -> isMobile = matchesWidth && deviceType === "mobile"
 *    detailed: wenn true -> detailliertes Objekt statt Boolean zurückgeben
 *
 * Rückgabe:
 *  - detailed=false: Boolean (isMobile)
 *  - detailed=true: {
 *      width, height, deviceType, matchesWidth, isTouch, isMobile
 *    }
 */
const useIsMobile = (
  breakpoint = 1040,
  { smart = false, detailed = false } = {}
) => {
  const [state, setState] = useState(() => ({
    width: 0,
    height: 0,
    deviceType: "unknown", // "mobile" | "tablet" | "desktop" | "unknown"
    matchesWidth: false,
    isTouch: false,
    isMobile: false,
  }));

  // Use layout effect in the browser so we can compute the correct layout *before paint*.
  // This avoids CLS when components change structure based on `isMobile` right after hydration.
  const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const getDeviceType = () => {
      const ua = navigator.userAgent || "";
      const uaDataMobile =
        typeof navigator.userAgentData?.mobile === "boolean"
          ? navigator.userAgentData.mobile
          : null;

      const isIPad =
        /iPad/.test(ua) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      const isAndroid = /Android/.test(ua);
      const isTabletUA =
        /Tablet|iPad/.test(ua) || (isAndroid && !/Mobile/.test(ua));
      const isMobileUA =
        /Mobi|iPhone|iPod|Phone/.test(ua) || uaDataMobile === true;

      const coarse =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(pointer: coarse)").matches;

      if (isIPad || isTabletUA) return "tablet";
      if (isMobileUA) return "mobile";
      if (coarse) {
        return window.innerWidth >= 768 ? "tablet" : "mobile";
      }
      return "desktop";
    };

    const update = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const matchesWidth = width <= breakpoint;

      // Nur berechnen, wenn benötigt (smart oder detailed)
      let deviceType = "unknown";
      let isTouch = false;
      let byType = false;

      if (smart || detailed) {
        deviceType = getDeviceType();
        isTouch =
          "ontouchstart" in window ||
          navigator.maxTouchPoints > 0 ||
          (typeof window.matchMedia === "function" &&
            window.matchMedia("(pointer: coarse)").matches);
        byType = deviceType === "mobile";
      }

      const isMobile = smart ? matchesWidth && byType : matchesWidth;

      setState({
        width,
        height,
        deviceType,
        matchesWidth,
        isTouch,
        isMobile,
      });
    };

    let raf = null;
    const onChange = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
    };
  }, [breakpoint, smart, detailed]);

  return detailed ? state : state.isMobile;
};

export default useIsMobile;
